/**
 * Setup Inspirasi: creator photos for the home page gallery strip.
 *
 * Every photo ships twice, a 720px square crop for the grid and an uncropped
 * `-full` version (1080px, the size Kanata supplies) that only loads when someone opens it.
 * Adding a creator is dropping `<slug>-<n>.webp` and `<slug>-<n>-full.webp`
 * into src/images/inspirasi and one line below with their name and handle.
 */

export interface Creator {
  slug: string;
  name: string;
  /** Instagram handle without the @, only where we know it for certain. */
  handle?: string;
}

export interface InspoPhoto {
  creator: Creator;
  thumb: string;
  full: string;
}

// Every photo has its creator's handle printed on it, so every creator here
// is credited by handle too.
const creators: Record<string, Omit<Creator, "slug">> = {
  ahmadtito: { name: "Ahmad Tito", handle: "_ahmadtito" },
  aldhirasaputra: { name: "Aldhira Saputra", handle: "aldhirasaputra" },
  alignleft: { name: "Alignleft", handle: "alignleft_" },
  badlan: { name: "Badlan", handle: "badlan_" },
  batuiceee: { name: "Batuice", handle: "batuiceee" },
  "bima-firmans": { name: "Bima Firmans", handle: "bima_firmans" },
  directedbyshidiq: { name: "Shidiq", handle: "directedbyshidiq" },
  edwardtanujayaa: { name: "Edward Tanujaya", handle: "edwardtanujayaa" },
  "hy-khal": { name: "Khal", handle: "hy.khal" },
  kanataworkspace: { name: "Kanata", handle: "kanataworkspace" },
  nandatamaaa: { name: "Nandatama", handle: "nandatamaaa" },
  nuramahouse: { name: "Nurama House", handle: "nuramahouse" },
  raihanslife: { name: "Raihanslife", handle: "raihanslife" },
  ryannifsya: { name: "Ryan Nifsya", handle: "ryannifsya" },
};

const files = import.meta.glob<string>("@/images/inspirasi/*.webp", {
  eager: true,
  import: "default",
});

/** Pair each thumb with its `-full` sibling, grouped by creator in photo order. */
export function collectPhotos(urls: Record<string, string>): InspoPhoto[][] {
  const byCreator = new Map<string, { n: number; photo: InspoPhoto }[]>();
  for (const [path, thumb] of Object.entries(urls)) {
    const match = /\/([a-z0-9-]+?)-(\d+)\.webp$/.exec(path);
    if (!match?.[1] || !match[2]) continue; // the -full files
    const slug = match[1];
    const full = urls[path.replace(/\.webp$/, "-full.webp")] ?? thumb;
    const creator = { slug, ...(creators[slug] ?? { name: slug }) };
    const list = byCreator.get(slug) ?? [];
    list.push({ n: Number(match[2]), photo: { creator, thumb, full } });
    byCreator.set(slug, list);
  }
  return [...byCreator.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, list]) => list.sort((a, b) => a.n - b.n).map((p) => p.photo));
}

/**
 * Every photo once, in rounds: every creator's first photo, then every
 * creator's second, and so on. Neighbours are as often as possible different
 * creators, and the opening screen shows as many different setups as fit.
 */
export function roundRobin(groups: InspoPhoto[][]): InspoPhoto[] {
  const rounds = Math.max(0, ...groups.map((g) => g.length));
  const out: InspoPhoto[] = [];
  for (let r = 0; r < rounds; r++) {
    for (const group of groups) {
      const photo = group[r];
      if (photo) out.push(photo);
    }
  }
  return out;
}

/**
 * Cut photos into columns of `rows`, the slides of the gallery strip: the
 * grid scrolls a column at a time, so it reads as rows of photos.
 */
export function intoColumns(photos: InspoPhoto[], rows: number): InspoPhoto[][] {
  const out: InspoPhoto[][] = [];
  for (let i = 0; i < photos.length; i += rows) out.push(photos.slice(i, i + rows));
  return out;
}

export const inspoPhotos = roundRobin(collectPhotos(files));

/** The gallery is two rows deep (Divine asked for 5x2, DEL-521). */
export const inspoColumns = intoColumns(inspoPhotos, 2);

export function instagramUrl(handle: string) {
  return `https://www.instagram.com/${handle}/`;
}
